import { z } from "zod";

// Shared type — used across the entire application
export type LocalizedString = {
  ro: string; // REQUIRED — primary platform language
  en: string; // REQUIRED — international fallback
  bg?: string;
  de?: string;
  fr?: string;
  it?: string;
  es?: string;
  cs?: string; // Czech
  hu?: string; // Hungarian
  hr?: string; // Croatian
  sr?: string; // Serbian
  _auto?: string[]; // Languages auto-translated by AI
  _original?: string; // Source language
  [key: string]: string | string[] | undefined;
};

// Zod validation (shared frontend + backend)
export const LocalizedStringSchema = z
  .object({
    ro: z.string().min(1, "Romanian translation is required"),
    en: z.string().min(1, "English translation is required"),
  })
  .catchall(z.union([z.string(), z.array(z.string())]).optional());

// Helper: get translation for current locale with fallback chain
export function t(field: LocalizedString | null | undefined, locale: string): string {
  if (!field) return "";
  return (
    (field[locale] as string) ||
    field.en ||
    field.ro ||
    (Object.values(field).find((v) => typeof v === "string" && v.length > 0) as string) ||
    ""
  );
}

// Check if a translation was auto-generated by AI
export function isAutoTranslated(field: LocalizedString, locale: string): boolean {
  return (field._auto || []).includes(locale);
}

// Mark a locale as manually edited (remove from auto list)
export function markAsManual(field: LocalizedString, locale: string): LocalizedString {
  const auto = (field._auto || []).filter((l) => l !== locale);
  return { ...field, _auto: auto.length > 0 ? auto : undefined };
}
